import { defineComponent as _defineComponent } from 'vue'
import {
    computed,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from "vue";
  import type { Ref } from "vue";
  import { useStore } from "vuex";
  import {
    ArrowRight,
    Check,
    Image as ImageIcon,
    Link as LinkIcon,
    Mail,
    MapPin,
    Pencil,
    Phone,
    Plus,
    Trash2,
    Upload,
    UserRound,
    X,
  } from "lucide-vue-next";
  import { getEstateMapPath } from "@/helpers/estateMapRoute";
  import projectPageApi, {
    LandSubdivisionContent,
    LandSubdivisionProjectDocument,
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import { EstateInterface } from "@/types/EstateInterface";

  type ModalType =
    | "hero"
    | "agency"
    | "about"
    | "features"
    | "document"
    | "contact"
    | "location"
    | "cta"
    | null;

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LandSubdivisionProjectPageEditor',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();
  const activeModal = ref<ModalType>(null);
  const loading = ref(false);
  const saving = ref(false);
  const copiedUrl = ref("");

  const estateId = computed<string>(() => store.state.user.selectedEstateId);
  const selectedEstate = computed<EstateInterface | undefined>(() =>
    store.state.user.landownerEstates.find(
      (estate: EstateInterface) => estate._id === estateId.value
    )
  );
  const estateAppVersion = computed(
    () => selectedEstate.value?.appVersion ?? "v2"
  );

  const defaultContent = (): LandSubdivisionContent => ({
    projectName: selectedEstate.value?.title ?? "",
    heroButtonColor: "#334e68",
    agency: { name: "", logoUrl: "", brandColor: "#1e3a5f" },
    aboutHeading: selectedEstate.value?.title
      ? `Welcome to ${selectedEstate.value.title}`
      : "",
    aboutDescription: "",
    features: [],
    documents: [],
    contact: {},
    locationAddress: "",
    callToAction: {
      heading: "Ready to Explore?",
      subtext:
        "Get in touch with our sales team to learn more about available lots and pricing.",
      buttonText: "View Land Availability",
      footerColor: "#1e293b",
    },
  });

  const pageData = reactive<ProjectPageData>({
    backgroundImageUrl: null,
    landSubContent: defaultContent(),
  });
  const content = computed(() => pageData.landSubContent ?? defaultContent());

  const heroForm = reactive({ projectName: "", buttonColor: "#334e68" });
  const agencyForm = reactive({ name: "", brandColor: "#1e3a5f" });
  const aboutForm = reactive({ heading: "", description: "" });
  const featuresText = ref("");
  const documentForm = reactive({
    id: "",
    name: "",
    description: "",
    fileName: "",
    fileUrl: "",
    mimetype: "",
  });
  const contactForm = reactive({ name: "", title: "", phone: "", email: "" });
  const locationAddress = ref("");
  const ctaForm = reactive({
    heading: "",
    subtext: "",
    buttonText: "",
    footerColor: "#1e293b",
  });

  const heroFile = ref<File | null>(null);
  const projectLogoFile = ref<File | null>(null);
  const agencyLogoFile = ref<File | null>(null);
  const documentFile = ref<File | null>(null);
  const contactPhotoFile = ref<File | null>(null);
  const heroFilePreviewUrl = ref("");
  const projectLogoFilePreviewUrl = ref("");
  const agencyLogoFilePreviewUrl = ref("");
  const contactPhotoFilePreviewUrl = ref("");

  const slug = computed(
    () =>
      pageData.slug ||
      (content.value.projectName || selectedEstate.value?.title || "project")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
  );
  const siteBase = computed(
    () => process.env.VUE_APP_WEB_URL || window.location.origin
  );
  const fullUrl = computed(() => `${siteBase.value}/p/${slug.value}`);
  const masterplanUrl = computed(() =>
    getEstateMapPath(estateId.value, selectedEstate.value?.type)
  );
  const backgroundPreviewUrl = computed(
    () => heroFilePreviewUrl.value || pageData.backgroundImageUrl || ""
  );
  const projectLogoPreviewUrl = computed(
    () => projectLogoFilePreviewUrl.value || content.value.projectLogoUrl || ""
  );
  const agencyLogoPreviewUrl = computed(
    () => agencyLogoFilePreviewUrl.value || content.value.agency?.logoUrl || ""
  );
  const contactPhotoPreviewUrl = computed(
    () =>
      contactPhotoFilePreviewUrl.value || content.value.contact?.photoUrl || ""
  );
  const backgroundDisplayName = computed(
    () =>
      heroFile.value?.name ||
      content.value.backgroundImageFileName ||
      fileNameFromUrl(pageData.backgroundImageUrl)
  );
  const projectLogoDisplayName = computed(
    () =>
      projectLogoFile.value?.name ||
      content.value.projectLogoFileName ||
      fileNameFromUrl(content.value.projectLogoUrl)
  );
  const agencyLogoDisplayName = computed(
    () =>
      agencyLogoFile.value?.name ||
      content.value.agency?.logoFileName ||
      fileNameFromUrl(content.value.agency?.logoUrl)
  );
  const contactPhotoDisplayName = computed(
    () =>
      contactPhotoFile.value?.name ||
      content.value.contact?.photoFileName ||
      fileNameFromUrl(content.value.contact?.photoUrl)
  );

  const aboutParagraphs = computed(() =>
    (content.value.aboutDescription || "")
      .split(/\n\s*\n/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean)
  );
  const heroStyle = computed(() =>
    pageData.backgroundImageUrl
      ? {
          backgroundImage: `linear-gradient(rgba(15, 23, 42, .38), rgba(15, 23, 42, .58)), url(${pageData.backgroundImageUrl})`,
        }
      : { background: "linear-gradient(135deg, #2f4058, #111a2d)" }
  );
  const heroButtonStyle = computed(() => ({
    backgroundColor: content.value.heroButtonColor || "#334e68",
  }));
  const agencyBarStyle = computed(() => ({
    backgroundColor: content.value.agency?.brandColor || "#1e3a5f",
  }));
  const contactCardStyle = computed(() => ({
    borderTopColor: content.value.agency?.brandColor || "#1e3a5f",
  }));
  const ctaStyle = computed(() => ({
    backgroundColor: content.value.callToAction?.footerColor || "#1e293b",
  }));
  const googleMapsUrl = computed(() =>
    content.value.locationAddress
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
          content.value.locationAddress
        )}`
      : "#"
  );
  const modalTitle = computed(
    () =>
      ({
        hero: "Edit Hero",
        agency: "Edit Agency Branding",
        about: "Edit About Section",
        features: "Edit Key Features",
        document: documentForm.id ? "Edit Document" : "Add Document",
        contact: "Edit Contact",
        location: "Edit Location",
        cta: "Edit Call to Action",
      }[activeModal.value || "hero"])
  );

  async function fetchPageData() {
    if (!estateId.value) return;

    loading.value = true;

    try {
      const { data } = await projectPageApi.getByEstateId(estateId.value);

      Object.assign(pageData, data);
      pageData.landSubContent = {
        ...defaultContent(),
        ...(data.landSubContent ?? {}),
      };
    } catch (error: any) {
      if (error?.response?.status === 404) {
        const { data } = await projectPageApi.upsert(
          estateId.value,
          estateAppVersion.value,
          { landSubContent: defaultContent() }
        );

        Object.assign(pageData, data);
      } else {
        throw error;
      }
    } finally {
      loading.value = false;
    }
  }

  onMounted(fetchPageData);
  watch(estateId, fetchPageData);
  onBeforeUnmount(clearImagePreviews);

  async function persistContent(next: LandSubdivisionContent) {
    const { data } = await projectPageApi.upsert(
      estateId.value,
      estateAppVersion.value,
      { landSubContent: next }
    );

    Object.assign(pageData, data);
  }

  function openModal(type: Exclude<ModalType, null>) {
    activeModal.value = type;

    if (type === "hero") {
      heroForm.projectName = content.value.projectName ?? "";
      heroForm.buttonColor = content.value.heroButtonColor || "#334e68";
    }

    if (type === "agency") {
      agencyForm.name = content.value.agency?.name ?? "";
      agencyForm.brandColor = content.value.agency?.brandColor || "#1e3a5f";
    }

    if (type === "about") {
      aboutForm.heading = content.value.aboutHeading ?? "";
      aboutForm.description = content.value.aboutDescription ?? "";
    }

    if (type === "features") {
      featuresText.value = (content.value.features ?? []).join("\n");
    }

    if (type === "contact")
      Object.assign(contactForm, content.value.contact ?? {});

    if (type === "location")
      locationAddress.value = content.value.locationAddress ?? "";

    if (type === "cta")
      Object.assign(ctaForm, content.value.callToAction ?? {});
  }

  function openDocumentModal(document?: LandSubdivisionProjectDocument) {
    Object.assign(documentForm, {
      id: document?.id ?? "",
      name: document?.name ?? "",
      description: document?.description ?? "",
      fileName: document?.fileName ?? "",
      fileUrl: document?.fileUrl ?? "",
      mimetype: document?.mimetype ?? "",
    });
    documentFile.value = null;
    activeModal.value = "document";
  }

  function closeModal() {
    activeModal.value = null;
    heroFile.value = null;
    projectLogoFile.value = null;
    agencyLogoFile.value = null;
    documentFile.value = null;
    contactPhotoFile.value = null;
    clearImagePreviews();
  }

  function fileNameFromUrl(url?: string | null) {
    if (!url) return "";

    try {
      const pathname = new URL(url, window.location.origin).pathname;

      return decodeURIComponent(
        pathname.split("/").filter(Boolean).pop() || ""
      );
    } catch {
      return "Current image";
    }
  }

  function updateImagePreview(preview: Ref<string>, file: File | null) {
    if (preview.value) URL.revokeObjectURL(preview.value);

    preview.value = file ? URL.createObjectURL(file) : "";
  }

  function clearImagePreviews() {
    [
      heroFilePreviewUrl,
      projectLogoFilePreviewUrl,
      agencyLogoFilePreviewUrl,
      contactPhotoFilePreviewUrl,
    ].forEach((preview) => updateImagePreview(preview, null));
  }

  function setFile(
    target: "hero" | "projectLogo" | "agencyLogo" | "document" | "contactPhoto",
    event: Event
  ) {
    const file = (event.target as HTMLInputElement).files?.[0] ?? null;

    if (target === "hero") {
      heroFile.value = file;
      updateImagePreview(heroFilePreviewUrl, file);
    }

    if (target === "projectLogo") {
      projectLogoFile.value = file;
      updateImagePreview(projectLogoFilePreviewUrl, file);
    }

    if (target === "agencyLogo") {
      agencyLogoFile.value = file;
      updateImagePreview(agencyLogoFilePreviewUrl, file);
    }

    if (target === "document") documentFile.value = file;

    if (target === "contactPhoto") {
      contactPhotoFile.value = file;
      updateImagePreview(contactPhotoFilePreviewUrl, file);
    }
  }

  async function saveActiveModal() {
    if (!activeModal.value || !estateId.value) return;

    saving.value = true;

    try {
      const next: LandSubdivisionContent = JSON.parse(
        JSON.stringify(content.value)
      );

      if (activeModal.value === "hero") {
        next.projectName = heroForm.projectName;
        next.heroButtonColor = heroForm.buttonColor;

        if (projectLogoFile.value) {
          const { data } = await projectPageApi.uploadLandSubdivisionAsset(
            estateId.value,
            "project-logo",
            projectLogoFile.value
          );

          next.projectLogoUrl = data.fileUrl;
          next.projectLogoFileName = data.fileName;
        }

        if (heroFile.value) {
          const { data } = await projectPageApi.uploadBackground(
            estateId.value,
            estateAppVersion.value,
            heroFile.value
          );

          pageData.backgroundImageUrl = data.backgroundImageUrl;
          next.backgroundImageFileName = heroFile.value.name;
        }
      }

      if (activeModal.value === "agency") {
        let logoUrl = next.agency?.logoUrl ?? "";
        let logoFileName = next.agency?.logoFileName ?? "";

        if (agencyLogoFile.value) {
          const { data } = await projectPageApi.uploadLandSubdivisionAsset(
            estateId.value,
            "agency-logo",
            agencyLogoFile.value
          );

          logoUrl = data.fileUrl;
          logoFileName = data.fileName;
        }

        next.agency = { ...agencyForm, logoUrl, logoFileName };
      }

      if (activeModal.value === "about") {
        next.aboutHeading = aboutForm.heading;
        next.aboutDescription = aboutForm.description;
      }

      if (activeModal.value === "features") {
        next.features = featuresText.value
          .split("\n")
          .map((feature) => feature.trim())
          .filter(Boolean);
      }

      if (activeModal.value === "document") {
        if (!documentForm.name) return;

        let asset = {
          fileName: documentForm.fileName,
          fileUrl: documentForm.fileUrl,
          mimetype: documentForm.mimetype,
        };

        if (documentFile.value) {
          const { data } = await projectPageApi.uploadLandSubdivisionAsset(
            estateId.value,
            "document",
            documentFile.value
          );

          asset = data;
        }

        if (!asset.fileUrl) return;

        const document: LandSubdivisionProjectDocument = {
          id:
            documentForm.id ||
            `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: documentForm.name,
          description: documentForm.description,
          ...asset,
        };
        const documents = [...(next.documents ?? [])];
        const existingIndex = documents.findIndex(
          (item) => item.id === document.id
        );

        if (existingIndex >= 0) documents.splice(existingIndex, 1, document);
        else documents.push(document);

        next.documents = documents;
      }

      if (activeModal.value === "contact") {
        let photoUrl = next.contact?.photoUrl ?? "";
        let photoFileName = next.contact?.photoFileName ?? "";

        if (contactPhotoFile.value) {
          const { data } = await projectPageApi.uploadLandSubdivisionAsset(
            estateId.value,
            "contact-photo",
            contactPhotoFile.value
          );

          photoUrl = data.fileUrl;
          photoFileName = data.fileName;
        }

        next.contact = { ...contactForm, photoUrl, photoFileName };
      }

      if (activeModal.value === "location")
        next.locationAddress = locationAddress.value;

      if (activeModal.value === "cta") next.callToAction = { ...ctaForm };

      await persistContent(next);
      closeModal();
    } finally {
      saving.value = false;
    }
  }

  async function deleteDocument(id: string) {
    if (!window.confirm("Delete this project-page document?")) return;

    await persistContent({
      ...content.value,
      documents: (content.value.documents ?? []).filter(
        (document) => document.id !== id
      ),
    });
  }

  async function copyUrl(key: string, value: string) {
    await navigator.clipboard.writeText(value);
    copiedUrl.value = key;
    window.setTimeout(() => (copiedUrl.value = ""), 1800);
  }

const __returned__ = { store, activeModal, loading, saving, copiedUrl, estateId, selectedEstate, estateAppVersion, defaultContent, pageData, content, heroForm, agencyForm, aboutForm, featuresText, documentForm, contactForm, locationAddress, ctaForm, heroFile, projectLogoFile, agencyLogoFile, documentFile, contactPhotoFile, heroFilePreviewUrl, projectLogoFilePreviewUrl, agencyLogoFilePreviewUrl, contactPhotoFilePreviewUrl, slug, siteBase, fullUrl, masterplanUrl, backgroundPreviewUrl, projectLogoPreviewUrl, agencyLogoPreviewUrl, contactPhotoPreviewUrl, backgroundDisplayName, projectLogoDisplayName, agencyLogoDisplayName, contactPhotoDisplayName, aboutParagraphs, heroStyle, heroButtonStyle, agencyBarStyle, contactCardStyle, ctaStyle, googleMapsUrl, modalTitle, fetchPageData, persistContent, openModal, openDocumentModal, closeModal, fileNameFromUrl, updateImagePreview, clearImagePreviews, setFile, saveActiveModal, deleteDocument, copyUrl, computed, onBeforeUnmount, onMounted, reactive, ref, watch, get useStore() { return useStore }, get ArrowRight() { return ArrowRight }, get Check() { return Check }, get ImageIcon() { return ImageIcon }, get LinkIcon() { return LinkIcon }, get Mail() { return Mail }, get MapPin() { return MapPin }, get Pencil() { return Pencil }, get Phone() { return Phone }, get Plus() { return Plus }, get Trash2() { return Trash2 }, get Upload() { return Upload }, get UserRound() { return UserRound }, get X() { return X }, get getEstateMapPath() { return getEstateMapPath }, get projectPageApi() { return projectPageApi }, get LandSubdivisionContent() { return LandSubdivisionContent }, get LandSubdivisionProjectDocument() { return LandSubdivisionProjectDocument }, get ProjectPageData() { return ProjectPageData }, get EstateInterface() { return EstateInterface } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})